import { isCanonicalInstantString } from "@nijiurachan/js/util/time";
import {
  type BoardPresentation,
  parseBoardPresentation,
} from "@/config/boardPresentation";
import {
  type BoardRuntimeConfig,
  getBoardPublicPath,
  type MixRuntimeConfig,
} from "@/config/boardRuntimeConfig";
import { parseDefaultDisplayName } from "@/config/defaultDisplayName";
import { formatBoardDisplayName } from "@/config/formatBoardDisplayName";
import { resolveMountedFrontendCanonicalPath } from "@/config/frontendRoute";
import { parseLocalApiBaseUrl } from "@/config/localApiBaseUrl";
import {
  filterMixLineupSlugs,
  isMixMountPath,
  isValidBoardSlug,
  MIX_PUBLIC_BASE_PATH,
} from "@/config/mixLineup";
import { API_VERSION, API_VERSION_HEADER } from "@/shared/api/version";

const RUNTIME_CONFIG_ELEMENT_ID = "aimg-runtime-config";
const BOARD_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;
const METADATA_REQUEST_TIMEOUT_MS = 10_000;
const METADATA_REQUEST_MAX_ATTEMPTS = 3;
const METADATA_RETRY_BASE_DELAY_MS = 250;

export type BoardMetadata = Readonly<{
  slug: string;
  boardName: string;
  createdAt: string;
  allowR18ThreadCreation: boolean;
  defaultDisplayName: string | null;
  presentation?: BoardPresentation;
}>;

export type BoardRuntimeDocument = Pick<
  Document,
  "createElement" | "getElementById" | "head" | "querySelector" | "title"
>;

export type BoardRuntimeDependencies = Readonly<{
  apiVersion: string;
  apiVersionHeader: string;
  fetcher: typeof fetch;
  formatBoardDisplayName: (source: Readonly<{ boardName: string }>) => string;
  parseApiBaseUrl: (value: string) => string | null;
  createTimeoutSignal?: (timeoutMs: number) => AbortSignal;
  waitBeforeRetry?: (delayMs: number) => Promise<void>;
}>;

const createTimeoutSignal = (timeoutMs: number): AbortSignal =>
  AbortSignal.timeout(timeoutMs);
const waitBeforeRetry = (delayMs: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, delayMs));

const DEFAULT_DEPENDENCIES: BoardRuntimeDependencies = Object.freeze({
  apiVersion: API_VERSION,
  apiVersionHeader: API_VERSION_HEADER,
  fetcher: fetch,
  formatBoardDisplayName,
  parseApiBaseUrl: parseLocalApiBaseUrl,
  createTimeoutSignal,
  waitBeforeRetry,
});

function isRetryableMetadataStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

function isRetryableMetadataError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return false;
  }
  return error.name === "TimeoutError";
}

/**
 * Fetch metadata with at most three fresh 10-second request budgets.
 * The two bounded delays keep the total retry window at or below 30.75 seconds.
 */
async function fetchMetadataJsonResponse(
  url: URL,
  dependencies: BoardRuntimeDependencies,
  invalidJsonMessage: string,
  attempt = 1,
): Promise<{ response: Response; value?: unknown }> {
  let response: Response;
  try {
    response = await dependencies.fetcher.call(globalThis, url, {
      headers: {
        Accept: "application/json",
        [dependencies.apiVersionHeader]: dependencies.apiVersion,
      },
      signal: (dependencies.createTimeoutSignal ?? createTimeoutSignal)(
        METADATA_REQUEST_TIMEOUT_MS,
      ),
    });
  } catch (error) {
    if (
      !isRetryableMetadataError(error) ||
      attempt >= METADATA_REQUEST_MAX_ATTEMPTS
    ) {
      throw error;
    }
    await (dependencies.waitBeforeRetry ?? waitBeforeRetry)(
      METADATA_RETRY_BASE_DELAY_MS * attempt,
    );
    return fetchMetadataJsonResponse(
      url,
      dependencies,
      invalidJsonMessage,
      attempt + 1,
    );
  }

  if (
    !isRetryableMetadataStatus(response.status) ||
    attempt >= METADATA_REQUEST_MAX_ATTEMPTS
  ) {
    if (!response.ok) return { response };
    try {
      return { response, value: await response.json() };
    } catch (error) {
      if (
        !isRetryableMetadataError(error) ||
        attempt >= METADATA_REQUEST_MAX_ATTEMPTS
      ) {
        if (isRetryableMetadataError(error)) throw error;
        throw new Error(invalidJsonMessage);
      }
      await (dependencies.waitBeforeRetry ?? waitBeforeRetry)(
        METADATA_RETRY_BASE_DELAY_MS * attempt,
      );
      return fetchMetadataJsonResponse(
        url,
        dependencies,
        invalidJsonMessage,
        attempt + 1,
      );
    }
  }
  await (dependencies.waitBeforeRetry ?? waitBeforeRetry)(
    METADATA_RETRY_BASE_DELAY_MS * attempt,
  );
  return fetchMetadataJsonResponse(
    url,
    dependencies,
    invalidJsonMessage,
    attempt + 1,
  );
}

/** Extract and validate the exact board slug at the start of a `/b/{slug}` URL. */
export function parseBoardSlug(pathname: string): string | null {
  const match = /^\/b\/([^/]+)(?:\/|$)/.exec(pathname);
  const slug = match?.[1];
  return slug && BOARD_SLUG_PATTERN.test(slug) ? slug : null;
}

export function resolveInjectedBoardRuntimeConfig(
  documentLike: Pick<Document, "getElementById"> | undefined,
  pathname: string,
  locationOrigin: string,
): BoardRuntimeConfig | null {
  const injected = readInjectedBoardRuntimeConfig(documentLike);
  if (injected === null) return null;

  const slug = parseBoardSlug(pathname);
  if (slug) {
    return !injected.mix &&
      injected.boardKey === slug &&
      injected.publicBasePath === `/b/${slug}`
      ? injected
      : null;
  }
  if (isMixMountPath(pathname)) {
    return injected.mix && injected.publicBasePath === MIX_PUBLIC_BASE_PATH
      ? injected
      : null;
  }
  if (pathname === "/b" || pathname.startsWith("/b/")) return null;

  const currentOrigin = parseLocalApiBaseUrl(locationOrigin);
  return !injected.mix &&
    injected.publicBasePath === "" &&
    injected.apiBaseUrl === currentOrigin
    ? injected
    : null;
}

function readInjectedBoardRuntimeConfig(
  documentLike: Pick<Document, "getElementById"> | undefined,
): BoardRuntimeConfig | null {
  const source = documentLike?.getElementById(
    RUNTIME_CONFIG_ELEMENT_ID,
  )?.textContent;
  if (!source) return null;
  try {
    return parseBoardRuntimeConfig(JSON.parse(source));
  } catch {
    return null;
  }
}

export function parseBoardRuntimeConfig(
  value: unknown,
): BoardRuntimeConfig | null {
  if (!isRecord(value)) return null;
  const config = value;
  const apiBaseUrl =
    typeof config.apiBaseUrl === "string"
      ? parseLocalApiBaseUrl(config.apiBaseUrl)
      : null;
  if (
    typeof config.boardName !== "string" ||
    config.boardName.trim() === "" ||
    typeof config.boardKey !== "string" ||
    !BOARD_SLUG_PATTERN.test(config.boardKey) ||
    typeof config.publicBasePath !== "string" ||
    !apiBaseUrl
  ) {
    return null;
  }
  const mix = parseMixRuntimeConfig(config.mix, config.boardKey);
  if (mix === null) return null;
  if (
    mix === undefined
      ? config.publicBasePath !== "" &&
        config.publicBasePath !== `/b/${config.boardKey}`
      : config.publicBasePath !== MIX_PUBLIC_BASE_PATH
  ) {
    return null;
  }
  return Object.freeze({
    boardName: config.boardName,
    boardKey: config.boardKey,
    publicBasePath: config.publicBasePath,
    apiBaseUrl,
    ...(config.defaultDisplayName === undefined
      ? {}
      : {
          defaultDisplayName: parseDefaultDisplayName(
            config.defaultDisplayName,
          ),
        }),
    ...(mix ? { mix } : {}),
  });
}

function parseMixRuntimeConfig(
  value: unknown,
  boardKey: string,
): MixRuntimeConfig | undefined | null {
  if (value === undefined) return undefined;
  if (!isRecord(value)) return null;
  const { registeredSlugs } = value;
  if (
    !Array.isArray(registeredSlugs) ||
    registeredSlugs[0] !== boardKey ||
    registeredSlugs.some(
      (slug) => typeof slug !== "string" || !BOARD_SLUG_PATTERN.test(slug),
    )
  ) {
    return null;
  }
  const boardNames = parseBoardNames(value.boardNames);
  if (boardNames === null) return null;
  return Object.freeze({
    registeredSlugs: Object.freeze([...registeredSlugs]),
    ...(value.boardDefaultDisplayNames === undefined
      ? {}
      : {
          boardDefaultDisplayNames: Object.freeze(
            Object.fromEntries(
              registeredSlugs.map((slug) => [
                slug,
                parseDefaultDisplayName(
                  isRecord(value.boardDefaultDisplayNames)
                    ? value.boardDefaultDisplayNames[slug]
                    : undefined,
                ),
              ]),
            ),
          ),
        }),
    ...(boardNames ? { boardNames } : {}),
    ...(value.boardPresentations === undefined
      ? {}
      : {
          boardPresentations: Object.freeze(
            Object.fromEntries(
              registeredSlugs.map((slug) => [
                slug,
                parseBoardPresentation(
                  isRecord(value.boardPresentations)
                    ? value.boardPresentations[slug]
                    : undefined,
                ),
              ]),
            ),
          ),
        }),
  });
}

function parseBoardNames(
  value: unknown,
): Readonly<Record<string, string>> | undefined | null {
  if (value === undefined) return undefined;
  if (!isRecord(value)) return null;
  const names: Record<string, string> = {};
  for (const [slug, name] of Object.entries(value)) {
    if (
      !BOARD_SLUG_PATTERN.test(slug) ||
      typeof name !== "string" ||
      name.trim() === ""
    ) {
      return null;
    }
    names[slug] = name;
  }
  return Object.freeze(names);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Validate the metadata response shared by runtime initialization tests and fetches. */
export function parseBoardMetadata(
  value: unknown,
  expectedSlug: string,
): BoardMetadata | null {
  if (!isRecord(value)) return null;
  const metadata = value;
  if (
    metadata.slug !== expectedSlug ||
    typeof metadata.boardName !== "string" ||
    metadata.boardName.trim() === "" ||
    typeof metadata.allowR18ThreadCreation !== "boolean" ||
    !isCanonicalInstantString(metadata.createdAt)
  ) {
    return null;
  }

  return Object.freeze({
    slug: expectedSlug,
    boardName: metadata.boardName,
    createdAt: metadata.createdAt,
    allowR18ThreadCreation: metadata.allowR18ThreadCreation,
    defaultDisplayName: parseDefaultDisplayName(metadata.defaultDisplayName),
    presentation: parseBoardPresentation(metadata.presentation),
  });
}

/** Validate the `GET /boards` list response used by the mix catalog bootstrap. */
export function parseBoardList(
  value: unknown,
): readonly BoardMetadata[] | null {
  if (!isRecord(value)) return null;
  const boards = value.boards;
  if (!Array.isArray(boards)) return null;

  const entries: BoardMetadata[] = [];
  for (const item of boards) {
    if (!isRecord(item)) return null;
    const record = item;
    if (
      typeof record.slug !== "string" ||
      !BOARD_SLUG_PATTERN.test(record.slug) ||
      typeof record.boardName !== "string" ||
      record.boardName.trim() === "" ||
      typeof record.allowR18ThreadCreation !== "boolean" ||
      !isCanonicalInstantString(record.createdAt)
    ) {
      return null;
    }
    entries.push(
      Object.freeze({
        slug: record.slug,
        boardName: record.boardName,
        createdAt: record.createdAt,
        allowR18ThreadCreation: record.allowR18ThreadCreation,
        defaultDisplayName: parseDefaultDisplayName(record.defaultDisplayName),
        presentation: parseBoardPresentation(record.presentation),
      }),
    );
  }
  return Object.freeze(entries);
}

/**
 * `/mix` 用に板一覧を取得し、ラインナップ交差と先頭アンカー板でconfigを構築する。
 * 総合カタログ対象が1板も登録されていない環境では既存のbootstrap失敗UIへ落とす。
 */
async function fetchMixRuntimeConfig(
  apiBaseUrl: string,
  dependencies: BoardRuntimeDependencies,
  pathname: string,
): Promise<BoardRuntimeConfig> {
  const listUrl = new URL("/boards", apiBaseUrl);
  const { response, value } = await fetchMetadataJsonResponse(
    listUrl,
    dependencies,
    "Board list response for mix catalog is not JSON",
  );
  if (!response.ok) {
    throw new Error(
      `Board list request failed for mix catalog (HTTP ${response.status})`,
    );
  }

  const boards = parseBoardList(value);
  if (boards === null) {
    throw new Error("Board list response for mix catalog is invalid");
  }

  const registeredSlugs = filterMixLineupSlugs(
    boards.map((board) => board.slug),
  );
  for (const board of boards) {
    if (!registeredSlugs.includes(board.slug)) registeredSlugs.push(board.slug);
  }
  const threadSlug = /^\/mix\/([^/]+)\/thread\/[^/]+\/?$/.exec(pathname)?.[1];
  if (threadSlug !== undefined) {
    let slug: string;
    try {
      slug = decodeURIComponent(threadSlug);
    } catch {
      throw new Error("Invalid mix thread board slug");
    }
    if (
      !isValidBoardSlug(slug) ||
      !boards.some((board) => board.slug === slug)
    ) {
      throw new Error("Mix thread board is not registered");
    }
    if (!registeredSlugs.includes(slug)) registeredSlugs.push(slug);
  }
  const host = boards.find((board) => board.slug === registeredSlugs[0]);
  if (host === undefined) {
    throw new Error("Mix catalog has no registered lineup board");
  }

  const boardNames = Object.freeze(
    Object.fromEntries(
      boards
        .filter((board) => registeredSlugs.includes(board.slug))
        .map((board) => [board.slug, board.boardName]),
    ),
  );

  return Object.freeze({
    boardName: host.boardName,
    boardKey: host.slug,
    publicBasePath: MIX_PUBLIC_BASE_PATH,
    apiBaseUrl,
    mix: Object.freeze({
      registeredSlugs: Object.freeze(registeredSlugs),
      boardNames,
      boardDefaultDisplayNames: Object.freeze(
        Object.fromEntries(
          boards.map((board) => [board.slug, board.defaultDisplayName ?? null]),
        ),
      ),
      boardPresentations: Object.freeze(
        Object.fromEntries(
          boards.map((board) => [
            board.slug,
            parseBoardPresentation(board.presentation),
          ]),
        ),
      ),
    }),
  });
}

/** Fetch validated board metadata and construct the synchronous browser config. */
export async function fetchBoardRuntimeConfig(
  pathname: string,
  configuredApiBaseUrl: string,
  dependencies: BoardRuntimeDependencies = DEFAULT_DEPENDENCIES,
): Promise<BoardRuntimeConfig> {
  if (isMixMountPath(pathname)) {
    const mixApiBaseUrl = dependencies.parseApiBaseUrl(configuredApiBaseUrl);
    if (mixApiBaseUrl === null || mixApiBaseUrl === "") {
      throw new Error("VITE_BASE_URL must be an absolute HTTP(S) API origin");
    }
    return fetchMixRuntimeConfig(mixApiBaseUrl, dependencies, pathname);
  }

  const slug = parseBoardSlug(pathname);
  if (slug === null) {
    throw new Error("Browser URL must start with /b/{valid-slug}");
  }

  const apiBaseUrl = dependencies.parseApiBaseUrl(configuredApiBaseUrl);
  if (apiBaseUrl === null || apiBaseUrl === "") {
    throw new Error("VITE_BASE_URL must be an absolute HTTP(S) API origin");
  }

  const metadataUrl = new URL(
    `/boards/${encodeURIComponent(slug)}`,
    apiBaseUrl,
  );
  const { response, value } = await fetchMetadataJsonResponse(
    metadataUrl,
    dependencies,
    `Board metadata response for ${slug} is not JSON`,
  );
  if (!response.ok) {
    throw new Error(
      `Board metadata request failed for ${slug} (HTTP ${response.status})`,
    );
  }

  const metadata = parseBoardMetadata(value, slug);
  if (metadata === null) {
    throw new Error(`Board metadata response for ${slug} is invalid`);
  }

  return Object.freeze({
    boardName: metadata.boardName,
    boardKey: metadata.slug,
    defaultDisplayName: metadata.defaultDisplayName ?? null,
    publicBasePath: `/b/${metadata.slug}`,
    apiBaseUrl,
  });
}

/** Inject runtime config and restore metadata omitted by a cached initial HTML. */
export function applyBoardRuntimeConfig(
  config: BoardRuntimeConfig,
  documentLike: BoardRuntimeDocument,
  locationOrigin: string,
  dependencies: Pick<
    BoardRuntimeDependencies,
    "formatBoardDisplayName"
  > = DEFAULT_DEPENDENCIES,
  pathname: string = getBoardPublicPath(config, "/"),
): void {
  const runtimeElement =
    documentLike.getElementById(RUNTIME_CONFIG_ELEMENT_ID) ??
    appendHeadElement(documentLike, "script", {
      id: RUNTIME_CONFIG_ELEMENT_ID,
      type: "application/json",
    });
  let canonical = documentLike.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  const appleTitle =
    documentLike.querySelector<HTMLMetaElement>(
      'meta[name="apple-mobile-web-app-title"]',
    ) ??
    appendHeadElement(documentLike, "meta", {
      name: "apple-mobile-web-app-title",
    });
  const manifest =
    documentLike.querySelector<HTMLLinkElement>('link[rel="manifest"]') ??
    appendHeadElement(documentLike, "link", { rel: "manifest" });

  const displayName = dependencies.formatBoardDisplayName(config);
  const canonicalPath = resolveMountedFrontendCanonicalPath(config, pathname);
  runtimeElement.textContent = serializeRuntimeConfig(config);
  documentLike.title = displayName;
  if (canonicalPath === null) {
    canonical?.remove();
  } else {
    const robots = documentLike.querySelector<HTMLMetaElement>(
      'meta[name="robots" i]',
    );
    // SSR error shells deliberately omit canonical; cached index shells do not.
    if (canonical === null && !/\bnoindex\b/i.test(robots?.content ?? "")) {
      canonical = appendHeadElement(documentLike, "link", { rel: "canonical" });
    }
    if (canonical !== null) {
      canonical.href = new URL(canonicalPath, `${locationOrigin}/`).toString();
    }
  }
  appleTitle.content = displayName;
  // 共有パス /b/manifest.json は未レンダリングの生manifest(start_url=/b/は404)を
  // 返すため、Workerが板別に書き換える公開パスを指し直す。
  manifest.href = getBoardPublicPath(config, "/manifest.json");
}

function appendHeadElement<K extends keyof HTMLElementTagNameMap>(
  documentLike: BoardRuntimeDocument,
  tagName: K,
  attributes: Readonly<Record<string, string>>,
): HTMLElementTagNameMap[K] {
  const element = documentLike.createElement(tagName);
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
  documentLike.head.append(element);
  return element;
}

/** Initialize the current `/b/{slug}` before any application module is imported. */
export async function initializeBoardRuntime(
  configuredApiBaseUrl: string = import.meta.env.VITE_BASE_URL ?? "",
  pathname: string = globalThis.location.pathname,
  locationOrigin: string = globalThis.location.origin,
  documentLike: BoardRuntimeDocument = globalThis.document,
  dependencies: BoardRuntimeDependencies = DEFAULT_DEPENDENCIES,
): Promise<BoardRuntimeConfig> {
  const injected = resolveInjectedBoardRuntimeConfig(
    documentLike,
    pathname,
    locationOrigin,
  );
  const localPreview = ["localhost", "127.0.0.1", "[::1]"].includes(
    new URL(locationOrigin).hostname,
  );
  const matchesLocalApi =
    injected?.publicBasePath === "" ||
    !localPreview ||
    configuredApiBaseUrl.trim() === "" ||
    injected?.apiBaseUrl === dependencies.parseApiBaseUrl(configuredApiBaseUrl);
  if (injected !== null && matchesLocalApi) {
    applyBoardRuntimeConfig(
      injected,
      documentLike,
      locationOrigin,
      dependencies,
      pathname,
    );
    return injected;
  }
  const config = await fetchBoardRuntimeConfig(
    pathname,
    configuredApiBaseUrl,
    dependencies,
  );
  applyBoardRuntimeConfig(
    config,
    documentLike,
    locationOrigin,
    dependencies,
    pathname,
  );
  return config;
}

function serializeRuntimeConfig(config: BoardRuntimeConfig): string {
  return JSON.stringify(config)
    .replaceAll("<", "\\u003c")
    .replaceAll(">", "\\u003e")
    .replaceAll("&", "\\u0026")
    .replaceAll("\u2028", "\\u2028")
    .replaceAll("\u2029", "\\u2029");
}
